// Contrail V3.11.0 — platform status notices (maintenance, upgrades, service issues).
// Shared by the browser (banner, Admin → Status) and the Netlify function (netlify/lib/statusCore.ts).
// Notices are stored in Netlify Blobs, NOT in Supabase, so they still work when the database is slow or down.

export const NOTICE_LEVELS = ["maintenance", "upgrade", "issue", "info"] as const;
export type NoticeLevel = (typeof NOTICE_LEVELS)[number];

export const LEVEL_LABEL: Record<NoticeLevel, string> = {
  maintenance: "Planned maintenance",
  upgrade: "Upgrade in progress",
  issue: "Service issue",
  info: "Notice",
};

export type Notice = {
  id: string;
  level: NoticeLevel;
  title: string;                 // short headline, e.g. "Platform upgrades"
  message: string;               // one or two plain sentences
  starts_at: string | null;      // window start (ISO). null = already in effect
  ends_at: string | null;        // window end (ISO): hidden automatically after this. null = until an admin ends it
  announce_from: string | null;  // shown from (ISO). null = straight away
  show_app: boolean;             // banner on every app page
  show_login: boolean;           // box on the sign-in page
  created_at: string; created_by: string;
  updated_at: string; updated_by: string;
  ended_at: string | null;       // ended by an admin
};
/** What anyone (signed in or not) receives from GET /api/status. */
export type PublicNotice = Pick<Notice, "id" | "level" | "title" | "message" | "starts_at" | "ends_at" | "show_app" | "show_login" | "updated_at">;

export const MAX_TITLE = 80;
export const MAX_MESSAGE = 600;

const t = (iso: string | null) => (iso ? Date.parse(iso) : NaN);

/** Shown right now? (not ended, not past its end, and its announce time has come) */
export function isVisible(n: Pick<Notice, "ended_at" | "ends_at" | "announce_from">, now = Date.now()) {
  if (n.ended_at) return false;
  if (n.ends_at && now >= t(n.ends_at)) return false;
  if (n.announce_from && now < t(n.announce_from)) return false;
  return true;
}

/** "upcoming" before the window starts, otherwise "now". */
export function phase(n: Pick<Notice, "starts_at">, now = Date.now()): "upcoming" | "now" {
  return n.starts_at && now < t(n.starts_at) ? "upcoming" : "now";
}

/** Status of a notice for the admin list. */
export function adminState(n: Notice, now = Date.now()): "live" | "scheduled" | "ended" {
  if (n.ended_at || (n.ends_at && now >= t(n.ends_at))) return "ended";
  if (n.announce_from && now < t(n.announce_from)) return "scheduled";
  return "live";
}

// ---------------------------------------------------------------- Fiji time formatting
const FIJI = "Pacific/Fiji";
const dayFmt = new Intl.DateTimeFormat("en-GB", { timeZone: FIJI, weekday: "short", day: "numeric", month: "short" });
const timeFmt = new Intl.DateTimeFormat("en-GB", { timeZone: FIJI, hour: "2-digit", minute: "2-digit", hour12: false });
const fDay = (ms: number) => dayFmt.format(ms).replace(",", "");
const fTime = (ms: number) => timeFmt.format(ms);

/** e.g. "Sun 26 Oct, 20:00–22:00 (Fiji time)" · "Until Sun 26 Oct, 22:00 (Fiji time)" · "" */
export function windowText(n: Pick<Notice, "starts_at" | "ends_at">) {
  const s = t(n.starts_at), e = t(n.ends_at);
  if (!isNaN(s) && !isNaN(e)) {
    return fDay(s) === fDay(e)
      ? `${fDay(s)}, ${fTime(s)}–${fTime(e)} (Fiji time)`
      : `${fDay(s)} ${fTime(s)} – ${fDay(e)} ${fTime(e)} (Fiji time)`;
  }
  if (!isNaN(e)) return `Until ${fDay(e)}, ${fTime(e)} (Fiji time)`;
  if (!isNaN(s)) return `From ${fDay(s)}, ${fTime(s)} (Fiji time)`;
  return "";
}

/** <input type="datetime-local"> value (Fiji time) ↔ ISO. Fiji is UTC+12 (no daylight saving at present). */
export function fijiLocalToIso(v: string): string | null {
  if (!v) return null;
  const ms = Date.parse(`${v.length === 16 ? v + ":00" : v}+12:00`);
  return isNaN(ms) ? null : new Date(ms).toISOString();
}
export function isoToFijiLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(Date.parse(iso) + 12 * 3600_000);
  return d.toISOString().slice(0, 16);
}

// ---------------------------------------------------------------- input check (server is authoritative; the form uses it too)
export type NoticeInput = {
  level: NoticeLevel; title: string; message: string;
  starts_at: string | null; ends_at: string | null; announce_from: string | null;
  show_app: boolean; show_login: boolean;
};

/** Returns a clean NoticeInput or throws Error(message in plain words). */
export function checkNoticeInput(b: Record<string, unknown>, { now = Date.now(), editing = false } = {}): NoticeInput {
  const level = b.level as NoticeLevel;
  if (!NOTICE_LEVELS.includes(level)) throw new Error("Choose the type of notice");
  const title = typeof b.title === "string" ? b.title.trim() : "";
  const message = typeof b.message === "string" ? b.message.trim() : "";
  if (!title) throw new Error("Add a short title");
  if (title.length > MAX_TITLE) throw new Error(`Keep the title under ${MAX_TITLE} characters`);
  if (!message) throw new Error("Add a message for users");
  if (message.length > MAX_MESSAGE) throw new Error(`Keep the message under ${MAX_MESSAGE} characters`);
  const date = (k: string) => {
    const v = b[k];
    if (v === null || v === undefined || v === "") return null;
    if (typeof v !== "string" || isNaN(Date.parse(v))) throw new Error(`Invalid ${k.replace(/_/g, " ")}`);
    return new Date(Date.parse(v)).toISOString();
  };
  const starts_at = date("starts_at"), ends_at = date("ends_at"), announce_from = date("announce_from");
  if (starts_at && ends_at && t(ends_at) <= t(starts_at)) throw new Error("The end must be after the start");
  if (ends_at && t(ends_at) <= now && !editing) throw new Error("The end time has already passed");
  if (announce_from && starts_at && t(announce_from) > t(starts_at)) throw new Error("Start showing it before the window starts");
  if (announce_from && ends_at && t(announce_from) >= t(ends_at)) throw new Error("Start showing it before the window ends");
  const show_app = b.show_app !== false, show_login = b.show_login !== false;
  if (!show_app && !show_login) throw new Error("Show it in the app, on the sign-in page, or both");
  return { level, title, message, starts_at, ends_at, announce_from, show_app, show_login };
}

/** Ready-made wording (Kevin edits before posting). */
export const NOTICE_TEMPLATES: { label: string; level: NoticeLevel; title: string; message: string }[] = [
  { label: "Upgrades in progress", level: "upgrade", title: "Platform upgrades in progress",
    message: "We're upgrading Tracking Fiji. You may notice short interruptions or delayed live positions during this time. Your trips and history are not affected." },
  { label: "Planned maintenance", level: "maintenance", title: "Planned maintenance",
    message: "Tracking Fiji will have planned maintenance. The platform may be unavailable for short periods. Your trackers keep recording and history will catch up." },
  { label: "Live positions delayed", level: "issue", title: "Live positions are delayed",
    message: "Live positions are currently delayed. Your trackers are still recording and history is not affected. We're working on it." },
  { label: "New features", level: "info", title: "New features coming",
    message: "We're adding new features. You may need to refresh the page once the update is done." },
];
